/**
 * AirtablePeoplePage - Airtable-style admin view for all users
 * Shows users in a grid with expandable side panel for linked records
 * 
 * Features:
 * - Toggle between Table and Card views
 * - Field visibility controls (show/hide columns like Airtable)
 * - Side panel showing all linked data (preferences, RSVPs, orgs, etc.)
 * - Filter by tags, pilots, beehiiv status
 * - Bulk actions for Beehiiv sync
 * - Full edit capabilities for super admin
 * 
 * Last Updated: December 2025
 * Owner: Naomi (Super Admin)
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Users, Search, RefreshCw, Eye, EyeOff, ChevronUp, ChevronDown,
  Table2, LayoutGrid, Globe, Database, Trash2, Shield, Star,
  CheckCircle, XCircle, Filter, Download, Upload, Utensils, Accessibility,
  Heart, Scissors, Sparkles, Tag, Settings2, Columns, Mail, Building2,
  Calendar, Phone, Instagram, Linkedin, MapPin, Image, Edit2, Save, X,
  Shirt, HeartHandshake, UserPlus, Clock, MoreHorizontal, ArrowRightLeft, Link2
} from "lucide-react";
import UserDetailPanel from "./UserDetailPanel";
import OrgLinkedDashboardsPanel from "./OrgLinkedDashboardsPanel";
import UniversalPreferenceCardPopup from "@/components/UniversalPreferenceCardPopup";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ImageUpload from "@/components/ui/image-upload";
import WaitlistManager from "./WaitlistManager";
import HostDashboardsTab from "./HostDashboardsTab";
import PortalAccessTab from "./PortalAccessTab";
import MePilotsTab from "./MePilotsTab";
import InnerCircleSection from "../InnerCircleSection";
import InlineUserExpansion from "./InlineUserExpansion";
import RecordEditModal from "@/components/admin/RecordEditModal";
import HostPipelineKanban from "./HostPipelineKanban";
import AddHostModal from "./AddHostModal";
import CreateDashboardWizardModal from "@/components/admin/CreateDashboardWizardModal";
import { getCategoryPreset, getAllCategories } from "../../../lib/categoryConfig";

// Category icons for quick preference indicators
const CATEGORY_ICONS = {
  dietary: { icon: Utensils, color: "text-orange-500", bg: "bg-orange-50" },
  accessibility: { icon: Accessibility, color: "text-blue-500", bg: "bg-blue-50" },
  interests: { icon: Heart, color: "text-pink-500", bg: "bg-pink-50" },
  hair: { icon: Scissors, color: "text-purple-500", bg: "bg-purple-50" },
  skin: { icon: Sparkles, color: "text-rose-500", bg: "bg-rose-50" }
};

// Tier styles
const TIER_STYLES = {
  free: "bg-gray-100 text-gray-700",
  solo: "bg-blue-100 text-blue-700",
  team: "bg-purple-100 text-purple-700",
  luxe: "bg-amber-100 text-amber-700",
  enterprise: "bg-emerald-100 text-emerald-700"
};

// All available columns for field visibility
const ALL_COLUMNS = [
  { id: "name", label: "Name", required: true },
  { id: "email", label: "Email", required: false },
  { id: "phone", label: "Phone", required: false },
  { id: "company", label: "Company", required: false },
  { id: "tags", label: "Tags", required: false },
  { id: "preferences", label: "Preferences", required: false },
  { id: "beehiiv", label: "Beehiiv", required: false },
  { id: "created_at", label: "Joined", required: false },
  { id: "location", label: "Location", required: false },
  { id: "instagram", label: "Instagram", required: false },
  { id: "linkedin", label: "LinkedIn", required: false },
  { id: "events", label: "Events", required: false },
];

// Default visible columns
const DEFAULT_VISIBLE_COLUMNS = ["name", "email", "tags", "preferences", "beehiiv", "created_at"];

// Inline Org Events section - shows events created by this org with RSVP counts
function OrgEventsSection({ orgId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/organizations/${orgId}/events`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        });
        if (res.ok) {
          const data = await res.json();
          setEvents(data.events || []);
        }
      } catch (err) {
        console.error("Failed to fetch org events:", err);
      } finally {
        setLoading(false);
      }
    };
    if (orgId) fetchEvents();
  }, [orgId]);

  return (
    <div className="mt-4 pt-4 border-t" data-testid="org-events-section">
      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-purple-500" />
        Events
        <Badge variant="secondary" className="text-[10px]">{events.length}</Badge>
      </h4>
      {loading ? (
        <p className="text-xs text-gray-400">Loading events...</p>
      ) : events.length > 0 ? (
        <div className="space-y-2 max-h-[200px] overflow-y-auto">
          {events.map((event) => {
            const catPreset = getCategoryPreset(event.pilot_type || (event.categories || [])[0]);
            return (
              <div key={event.event_id} className="p-2.5 bg-gray-50 rounded-lg flex items-center justify-between hover:bg-gray-100">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: catPreset?.iconColor || "#9ca3af" }} />
                  <div className="min-w-0">
                    <span className="text-sm font-medium truncate block">{event.name || event.title || event.event_id}</span>
                    <span className="text-[10px] text-gray-400">
                      {event.start_date ? new Date(event.start_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "No date"}
                      {(event.categories || []).length > 0 && ` · ${event.categories.join(", ")}`}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{event.rsvp_count || 0} RSVPs</Badge>
                  {event.status && <Badge variant={event.status === "live" ? "default" : "secondary"} className="text-[10px]">{event.status}</Badge>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-gray-400 py-2">No events linked to this organization</p>
      )}
    </div>
  );
}

export default function AirtablePeoplePage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  // Iter 9.40 Slice C⁶ - universal preference card pop-up trigger from Clients table
  const [prefCardUser, setPrefCardUser] = useState(null);
  const [syncingBeehiiv, setSyncingBeehiiv] = useState(false);
  
  // View mode: "table" or "card"
  const [viewMode, setViewMode] = useState("table");
  
  // Field visibility (Airtable-style column control)
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  
  // Filters
  const [filterRole, setFilterRole] = useState("all");
  const [filterPilot, setFilterPilot] = useState("all");
  const [filterBeehiiv, setFilterBeehiiv] = useState("all");
  const [filterHasPrefs, setFilterHasPrefs] = useState("all");
  const [filterAccountType, setFilterAccountType] = useState("all"); // all, test, real
  
  // Bulk actions
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkTagging, setBulkTagging] = useState(false);
  
  // Sorting
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Panel width
  const [panelOpen, setPanelOpen] = useState(false);
  
  // Organization creation dialog
  const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", description: "", org_type: "company" });
  const [creatingOrg, setCreatingOrg] = useState(false);
  
  // Main view tab: "people" or "organizations"
  const [mainView, setMainView] = useState("people");
  
  // Organizations view state
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showAddHost, setShowAddHost] = useState(false);
  const [showCreateDashboard, setShowCreateDashboard] = useState(false);
  const [editOrgData, setEditOrgData] = useState({ name: "", description: "", website: "", logo_url: "" });
  const [savingOrg, setSavingOrg] = useState(false);
  const [orgSearch, setOrgSearch] = useState("");
  
  // Add member to org state
  const [showAddMemberSearch, setShowAddMemberSearch] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  
  // Dynamic tags from backend (for Fields & Tags tab)
  const [dynamicTags, setDynamicTags] = useState({ tags: [], categories: {} });
  const [pilotPortals, setPilotPortals] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  
  // Beehiiv custom fields
  const [beehiivFields, setBeehiivFields] = useState([]);
  const [beehiivFieldsLoading, setBeehiivFieldsLoading] = useState(false);
  
  // Create organization handler
  const handleCreateOrg = async () => {
    if (!newOrg.name.trim()) {
      toast.error("Organization name is required");
      return;
    }
    
    setCreatingOrg(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/organizations`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(newOrg)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Organization "${data.name}" created!`);
        setShowCreateOrgDialog(false);
        setNewOrg({ name: "", description: "", org_type: "company" });
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create organization");
      }
    } catch (err) {
      toast.error("Error creating organization");
    } finally {
      setCreatingOrg(false);
    }
  };
  
  // Fetch all organizations with member counts
  const fetchOrganizations = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const res = await fetch(`${API}/admin/organizations`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAllOrganizations(data.organizations || data || []);
      }
    } catch (err) {
      console.error("Failed to fetch organizations:", err);
    } finally {
      setOrgsLoading(false);
    }
  }, []);
  
  // Fetch members of a specific organization
  const fetchOrgMembers = useCallback(async (orgId) => {
    try {
      const res = await fetch(`${API}/admin/organizations/${orgId}/members`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setOrgMembers(data.members || []);
      }
    } catch (err) {
      console.error("Failed to fetch org members:", err);
      setOrgMembers([]);
    }
  }, []);
  
  // Update organization
  const handleUpdateOrg = async () => {
    if (!editingOrg) return;
    setSavingOrg(true);
    try {
      const res = await fetch(`${API}/admin/organizations/${editingOrg.org_id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(editOrgData)
      });
      if (res.ok) {
        toast.success("Organization updated");
        setEditingOrg(null);
        fetchOrganizations();
        if (selectedOrg?.org_id === editingOrg.org_id) {
          setSelectedOrg({ ...selectedOrg, ...editOrgData });
        }
      } else {
        toast.error("Failed to update organization");
      }
    } catch (err) {
      toast.error("Error updating organization");
    } finally {
      setSavingOrg(false);
    }
  };
  
  // Effect to fetch orgs when switching to organizations view
  useEffect(() => {
    if (mainView === "organizations" && allOrganizations.length === 0) {
      fetchOrganizations();
    }
  }, [mainView, fetchOrganizations, allOrganizations.length]);
  
  // Effect to fetch members when selecting an org
  useEffect(() => {
    if (selectedOrg) {
      fetchOrgMembers(selectedOrg.org_id);
    } else {
      setOrgMembers([]);
    }
  }, [selectedOrg, fetchOrgMembers]);
  
  // Search users to add to organization
  const searchUsersForOrg = async (query) => {
    if (!query || query.length < 2) {
      setMemberSearchResults([]);
      return;
    }
    setSearchingMembers(true);
    try {
      const res = await fetch(`${API}/admin/users/search?q=${encodeURIComponent(query)}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        // Filter out users already in the org
        const existingMemberIds = orgMembers.map(m => m.user_id);
        const filtered = (data.users || data || []).filter(u => !existingMemberIds.includes(u.user_id));
        setMemberSearchResults(filtered.slice(0, 10));
      }
    } catch (err) {
      console.log("User search error:", err);
    } finally {
      setSearchingMembers(false);
    }
  };
  
  // Add user to organization
  const handleAddMemberToOrg = async (user) => {
    if (!selectedOrg) return;
    setAddingMember(true);
    try {
      const res = await fetch(`${API}/admin/organizations/${selectedOrg.org_id}/members`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ user_id: user.user_id, role: "member" })
      });
      
      if (res.ok) {
        toast.success(`Added ${user.name || user.email} to ${selectedOrg.name}`);
        setShowAddMemberSearch(false);
        setMemberSearchQuery("");
        setMemberSearchResults([]);
        fetchOrgMembers(selectedOrg.org_id);
        fetchOrganizations(); // Update member counts
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to add member");
      }
    } catch (err) {
      toast.error("Error adding member");
    } finally {
      setAddingMember(false);
    }
  };
  
  // Remove user from organization
  const handleRemoveMemberFromOrg = async (userId, userName) => {
    if (!selectedOrg) return;
    if (!confirm(`Remove ${userName} from ${selectedOrg.name}?`)) return;
    
    try {
      const res = await fetch(`${API}/admin/organizations/${selectedOrg.org_id}/members/${userId}`, {
        method: "DELETE",
        headers: getHeaders()
      });
      
      if (res.ok) {
        toast.success(`Removed ${userName} from ${selectedOrg.name}`);
        fetchOrgMembers(selectedOrg.org_id);
        fetchOrganizations(); // Update member counts
      } else {
        toast.error("Failed to remove member");
      }
    } catch (err) {
      toast.error("Error removing member");
    }
  };
  
  // Bulk mark as test accounts
  const handleBulkMarkTest = async (isTest) => {
    if (selectedUserIds.length === 0) {
      toast.error("No users selected");
      return;
    }
    
    setBulkTagging(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/users/bulk-tag`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          user_ids: selectedUserIds,
          tag: "test_account",
          action: isTest ? "add" : "remove"
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${isTest ? "Marked" : "Unmarked"} ${data.updated || selectedUserIds.length} users as test accounts`);
        setSelectedUserIds([]);
        fetchUsers();
      } else {
        toast.error("Failed to update users");
      }
    } catch (err) {
      toast.error("Error updating users");
    } finally {
      setBulkTagging(false);
    }
  };
  
  // Bulk delete users
  const handleBulkDelete = async () => {
    if (selectedUserIds.length === 0) {
      toast.error("No users selected");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to DELETE ${selectedUserIds.length} users? This cannot be undone!`)) {
      return;
    }
    
    setBulkDeleting(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/users/bulk-delete`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ user_ids: selectedUserIds })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Deleted ${data.deleted || selectedUserIds.length} users`);
        setSelectedUserIds([]);
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete users");
      }
    } catch (err) {
      toast.error("Error deleting users");
    } finally {
      setBulkDeleting(false);
    }
  };
  
  // Toggle column visibility
  const toggleColumn = (columnId) => {
    const column = ALL_COLUMNS.find(c => c.id === columnId);
    if (column?.required) return; // Can't hide required columns
    
    setVisibleColumns(prev => 
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };
  
  // Check if column is visible
  const isColumnVisible = (columnId) => visibleColumns.includes(columnId);

  const token = localStorage.getItem("session_token");
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  });
  
  // Fetch dynamic tags from backend
  const fetchDynamicTags = useCallback(async () => {
    setTagsLoading(true);
    try {
      const res = await fetch(`${API}/admin/tags`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDynamicTags(data);
      }
    } catch (err) {
      console.error("Failed to fetch tags:", err);
    } finally {
      setTagsLoading(false);
    }
  }, []);
  
  // Fetch pilot portals for tag reference
  const fetchPilotPortals = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/portal-categories`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        const pilots = data.categories?.find(c => c.type === "pilots")?.sub_categories || [];
        setPilotPortals(pilots);
      }
    } catch (err) {
      console.error("Failed to fetch pilot portals:", err);
    }
  }, []);
  
  // Fetch Beehiiv custom fields
  const fetchBeehiivFields = useCallback(async () => {
    setBeehiivFieldsLoading(true);
    try {
      const res = await fetch(`${API}/beehiiv/custom-fields`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setBeehiivFields(data.fields || []);
      }
    } catch (err) {
      console.error("Failed to fetch Beehiiv fields:", err);
    } finally {
      setBeehiivFieldsLoading(false);
    }
  }, []);
  
  // Fetch tags, portals, and Beehiiv fields when switching to fields tab
  useEffect(() => {
    if (mainView === "fields") {
      fetchDynamicTags();
      fetchPilotPortals();
      fetchBeehiivFields();
    }
  }, [mainView, fetchDynamicTags, fetchPilotPortals, fetchBeehiivFields]);

  // Fetch users with preferences data
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Iter 9.39f: People → Users now reflects ONLY real /me accounts (db.users).
      // Virtual waitlist rows have moved to a dedicated WaitlistManager view so the
      // user table stays the canonical 15 (Naomi + pilots + 11 demos) the user expects.
      const res = await fetch(`${API}/admin/users?limit=500&include_profile=true`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        const usersData = data.users || [];

        // Fetch defaults for all users in parallel
        const defaultsRes = await fetch(`${API}/admin/profiles?limit=500`, { headers: getHeaders() });
        let defaultsMap = {};

        if (defaultsRes.ok) {
          const defaultsData = await defaultsRes.json();
          defaultsMap = (defaultsData.profiles || []).reduce((acc, p) => {
            acc[p.user_id] = {
              has_defaults: p.has_defaults,
              filled_categories: p.filled_categories || []
            };
            return acc;
          }, {});
        }

        const enrichedUsers = usersData
          .map(user => ({
            ...user,
            has_defaults: defaultsMap[user.user_id]?.has_defaults || false,
            filled_categories: defaultsMap[user.user_id]?.filled_categories || []
          }))
          .filter((u) => !u.is_archived);

        setUsers(enrichedUsers);
      }
    } catch (err) {
      console.error("Fetch users error:", err);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let result = users.filter(user => {
      // Search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!(
          (user.name || "").toLowerCase().includes(q) ||
          (user.email || "").toLowerCase().includes(q) ||
          (user.company || "").toLowerCase().includes(q) ||
          (user.tags || []).some(t => t.toLowerCase().includes(q))
        )) return false;
      }
      
      // Role filter
      if (filterRole !== "all") {
        if (filterRole === "admin" && !user.is_super_admin) return false;
        if (filterRole === "provider" && !user.is_provider) return false;
        if (filterRole === "host" && !(user.events_hosted > 0)) return false;
      }
      
      // Pilot filter
      if (filterPilot !== "all") {
        const pilotTags = (user.tags || []).filter(t => t.startsWith("pilot_"));
        if (filterPilot === "none" && pilotTags.length > 0) return false;
        if (filterPilot !== "none" && !pilotTags.includes(`pilot_${filterPilot}`)) return false;
      }
      
      // Beehiiv filter
      if (filterBeehiiv === "synced" && !user.beehiiv_subscriber_id) return false;
      if (filterBeehiiv === "not_synced" && user.beehiiv_subscriber_id) return false;
      
      // Has preferences filter
      if (filterHasPrefs === "yes" && !user.has_defaults) return false;
      if (filterHasPrefs === "no" && user.has_defaults) return false;
      
      // Test account filter
      if (filterAccountType === "test" && !(user.tags || []).includes("test_account")) return false;
      if (filterAccountType === "real" && (user.tags || []).includes("test_account")) return false;
      
      return true;
    });
    
    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === "created_at" || sortField === "updated_at") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      
      if (aVal === undefined || aVal === null) aVal = "";
      if (bVal === undefined || bVal === null) bVal = "";
      
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    
    return result;
  }, [users, searchQuery, filterRole, filterPilot, filterBeehiiv, filterHasPrefs, sortField, sortDirection]);

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Handle row click - toggle inline expansion
  const handleRowClick = (userId) => {
    if (selectedUserId === userId) {
      setSelectedUserId(null); // Collapse if clicking same row
    } else {
      setSelectedUserId(userId);
    }
  };

  // Select all/none
  const handleSelectAll = (checked) => {
    setSelectedUserIds(checked ? filteredUsers.map(u => u.user_id) : []);
  };

  // Toggle single user
  const handleToggleUser = (userId, e) => {
    e.stopPropagation();
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Bulk sync to Beehiiv
  const handleBulkSyncBeehiiv = async () => {
    if (selectedUserIds.length === 0) {
      toast.error("No users selected");
      return;
    }
    
    setSyncingBeehiiv(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const userId of selectedUserIds) {
      try {
        const res = await fetch(`${API}/admin/users/${userId}/sync-beehiiv`, {
          method: "POST",
          headers: getHeaders()
        });
        if (res.ok) successCount++;
        else failCount++;
      } catch {
        failCount++;
      }
    }
    
    setSyncingBeehiiv(false);
    toast.success(`Synced ${successCount} users${failCount > 0 ? `, ${failCount} failed` : ""}`);
    setSelectedUserIds([]);
    fetchUsers();
  };

  // Copy persistent magic link (Iter 9.40 - replaces View-as).
  // Returns/creates a never-expiring token per user; admin pastes into incognito.
  const handleCopyMagicLink = async (userId, userName) => {
    if (!userId) return;
    try {
      const res = await fetch(`${API}/admin/users/${userId}/magic-link`, {
        method: "POST",
        headers: getHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to fetch magic link");
        return;
      }
      const data = await res.json();
      const url = data?.magic_link_url || data?.url;
      if (!url) {
        toast.error("No magic link returned");
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Magic link for ${userName || userId} copied to clipboard`);
      } catch {
        // Clipboard may fail on insecure context - fall back to prompt
        window.prompt("Copy this magic link:", url);
      }
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Mask email
  const maskEmail = (email) => {
    if (!email || showSensitiveData) return email;
    const [local, domain] = email.split("@");
    if (!domain) return email;
    return `${local.slice(0, 2)}***@${domain}`;
  };

  // SortableHeader component
  const SortableHeader = ({ field, children, className = "" }) => (
    <th 
      className={`text-left p-2 font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none text-xs ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          sortDirection === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  return (
    <div className="h-full flex" data-testid="airtable-people-page">
      {/* Main content */}
      <div className="flex-1 flex min-w-0 transition-all duration-300">
        {/* Iter 9.39d - old inner sidebar removed; PeopleHub's top tabs are the single entry point. */}

        {/* Content area */}
        <div className="flex-1 flex flex-col min-w-0 p-4 md:p-6">
          {/* Header */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  People Database
                </h1>
                <p className="text-sm text-gray-500">
                  Manage users, organizations, fields, tags, and Beehiiv sync
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={fetchUsers}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>
            </div>
            
            {/* Iter 9.39d - mobile horizontal tabs also hidden; top-level PeopleHub tabs are authoritative. */}
            <div className="hidden">
              <Tabs value={mainView} onValueChange={setMainView} className="w-full">
                <TabsList className="flex w-full overflow-x-auto gap-1">
                  <TabsTrigger value="people" className="flex items-center gap-1.5 whitespace-nowrap" data-testid="people-tab">
                    <Users className="w-4 h-4" />
                    Users
                  </TabsTrigger>
                  <TabsTrigger value="organizations" className="flex items-center gap-1.5 whitespace-nowrap" data-testid="orgs-tab">
                    <Building2 className="w-4 h-4" />
                    Orgs
                  </TabsTrigger>
                  <TabsTrigger value="dashboards" className="flex items-center gap-1.5 whitespace-nowrap" data-testid="dashboards-tab">
                    <Settings2 className="w-4 h-4" />
                    Host Dashboards
                  </TabsTrigger>
                  <TabsTrigger value="me-pilots" className="flex items-center gap-1.5 whitespace-nowrap" data-testid="me-pilots-tab">
                    <Sparkles className="w-4 h-4" />
                    /me Pilots
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
          
          {/* Section content below */}
          
          {/* People View */}
          {mainView === "people" && (
            <>
              {/* People Header Controls */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <p className="text-sm text-gray-500">
                  {filteredUsers.length} of {users.length} users • Click row to view details
                </p>
                <div className="flex items-center gap-2 flex-wrap">
              {/* View Mode Toggle */}
              <div className="flex items-center border rounded-lg p-1 bg-gray-50" data-testid="view-mode-toggle">
                <Button
                  variant={viewMode === "table" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("table")}
                  className="h-7 px-2"
                  data-testid="table-view-btn"
                >
                  <Table2 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === "card" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("card")}
                  className="h-7 px-2"
                  data-testid="card-view-btn"
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Column Visibility (Airtable-style) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="column-visibility-btn">
                    <Columns className="w-4 h-4 mr-2" />
                    Fields
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Toggle Fields</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ALL_COLUMNS.map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      checked={isColumnVisible(column.id)}
                      disabled={column.required}
                      onCheckedChange={() => toggleColumn(column.id)}
                      data-testid={`toggle-column-${column.id}`}
                    >
                      {column.label}
                      {column.required && <Badge variant="secondary" className="ml-2 text-[10px]">Required</Badge>}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button 
                variant={showSensitiveData ? "default" : "outline"} 
                size="sm" 
                onClick={() => setShowSensitiveData(!showSensitiveData)}
                className={showSensitiveData ? "bg-amber-500 hover:bg-amber-600" : ""}
                data-testid="toggle-sensitive-data"
              >
                {showSensitiveData ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                {showSensitiveData ? "Data Visible" : "Data Hidden"}
              </Button>
              <Button variant="outline" size="sm" onClick={fetchUsers} disabled={loading} data-testid="refresh-users-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Bulk Actions Bar - Shows when users are selected */}
          {selectedUserIds.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-600">{selectedUserIds.length} selected</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedUserIds([])}
                >
                  Clear selection
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkMarkTest(true)}
                  disabled={bulkTagging}
                  data-testid="bulk-mark-test-btn"
                >
                  <Tag className="w-4 h-4 mr-1" />
                  Mark as Test
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkMarkTest(false)}
                  disabled={bulkTagging}
                  data-testid="bulk-unmark-test-btn"
                >
                  <Tag className="w-4 h-4 mr-1" />
                  Unmark Test
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {bulkDeleting ? "Deleting..." : "Delete Selected"}
                </Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <Card className="mb-4">
            <CardContent className="py-3">
              <div className="flex flex-wrap items-center gap-3">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search name, email, tags..."
                    className="pl-10"
                    data-testid="people-search"
                  />
                </div>

                {/* Role filter */}
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-[130px] h-9">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="provider">Provider</SelectItem>
                    <SelectItem value="host">Host</SelectItem>
                  </SelectContent>
                </Select>

                {/* Pilot filter */}
                <Select value={filterPilot} onValueChange={setFilterPilot}>
                  <SelectTrigger className="w-[130px] h-9">
                    <SelectValue placeholder="Pilot" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Pilots</SelectItem>
                    <SelectItem value="none">No Pilot</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="beauty">Beauty</SelectItem>
                    <SelectItem value="events">Events</SelectItem>
                    <SelectItem value="hthf_event">HTHF</SelectItem>
                  </SelectContent>
                </Select>

                {/* Beehiiv filter */}
                <Select value={filterBeehiiv} onValueChange={setFilterBeehiiv}>
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue placeholder="Beehiiv" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Beehiiv</SelectItem>
                    <SelectItem value="synced">Synced</SelectItem>
                    <SelectItem value="not_synced">Not Synced</SelectItem>
                  </SelectContent>
                </Select>

                {/* Preferences filter */}
                <Select value={filterHasPrefs} onValueChange={setFilterHasPrefs}>
                  <SelectTrigger className="w-[150px] h-9">
                    <SelectValue placeholder="Preferences" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="yes">Has Preferences</SelectItem>
                    <SelectItem value="no">No Preferences</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Account Type filter */}
                <Select value={filterAccountType} onValueChange={setFilterAccountType}>
                  <SelectTrigger className="w-[130px] h-9" data-testid="account-type-filter">
                    <SelectValue placeholder="Account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Accounts</SelectItem>
                    <SelectItem value="real">Real Users</SelectItem>
                    <SelectItem value="test">Test Accounts</SelectItem>
                  </SelectContent>
                </Select>

                {/* Bulk actions */}
                {selectedUserIds.length > 0 && (
                  <div className="flex items-center gap-2 ml-auto">
                    <Badge variant="outline">{selectedUserIds.length} selected</Badge>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedUserIds([])}>
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Users Display - Table or Card View */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-16">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-16">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No users found</p>
                </div>
              ) : viewMode === "table" ? (
                /* TABLE VIEW */
                <div className="overflow-x-auto">
                  <table className="w-full text-sm table-fixed" data-testid="users-table">
                    <thead className="bg-gray-50 border-b sticky top-0 z-10">
                      <tr>
                        <th className="w-10 p-2">
                          <Checkbox
                            checked={selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0}
                            onCheckedChange={handleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        {/* Dynamic columns based on visibility */}
                        {isColumnVisible("name") && <SortableHeader field="name" className="w-[180px]">Name</SortableHeader>}
                        {isColumnVisible("email") && <SortableHeader field="email" className="w-[180px]">Email</SortableHeader>}
                        {isColumnVisible("phone") && <th className="text-left p-2 font-medium text-gray-600 w-[110px]">Phone</th>}
                        {isColumnVisible("company") && <SortableHeader field="company" className="w-[120px]">Company</SortableHeader>}
                        {isColumnVisible("location") && <th className="text-left p-2 font-medium text-gray-600 w-[100px]">Location</th>}
                        {isColumnVisible("tags") && <th className="text-left p-2 font-medium text-gray-600 w-[140px]">Tags</th>}
                        {isColumnVisible("preferences") && <th className="text-left p-2 font-medium text-gray-600 w-[90px]">Prefs</th>}
                        {isColumnVisible("beehiiv") && (
                          <th className="text-left p-2 font-medium text-gray-600 w-[80px]">
                            <div className="flex items-center gap-1">
                              <img src="/assets/images/beehiiv-logo.jpeg" alt="Beehiiv" className="w-4 h-4 rounded-sm object-cover" />
                              Beehiiv
                            </div>
                          </th>
                        )}
                        {isColumnVisible("instagram") && <th className="text-left p-2 font-medium text-gray-600 w-[100px]">IG</th>}
                        {isColumnVisible("linkedin") && <th className="text-left p-2 font-medium text-gray-600 w-[70px]">LI</th>}
                        {isColumnVisible("events") && <th className="text-left p-2 font-medium text-gray-600 w-[90px]">Events</th>}
                        {isColumnVisible("created_at") && <SortableHeader field="created_at" className="w-[90px]">Joined</SortableHeader>}
                        <th className="text-right p-2 font-medium text-gray-600 w-[120px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredUsers.map(user => (
                        <React.Fragment key={user.user_id}>
                        <tr
                          onClick={() => handleRowClick(user.user_id)}
                          className={`
                            hover:bg-blue-50/50 cursor-pointer transition-colors
                            ${selectedUserId === user.user_id ? "bg-blue-50" : ""}
                            ${selectedUserIds.includes(user.user_id) ? "bg-blue-50/30" : ""}
                          `}
                          data-testid={`user-row-${user.user_id}`}
                        >
                          <td className="p-2" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedUserIds.includes(user.user_id)}
                              onCheckedChange={(e) => handleToggleUser(user.user_id, { stopPropagation: () => {} })}
                            />
                          </td>
                          {/* Name column - always visible */}
                          {isColumnVisible("name") && (
                            <td className="p-2">
                              <div className="flex items-center gap-1.5">
                                <Avatar className="h-7 w-7 shrink-0">
                                  <AvatarImage src={user.picture || user.profile_image} />
                                  <AvatarFallback className="bg-black text-white text-[10px]">
                                    {getInitials(user.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-900 truncate text-xs flex items-center gap-1">
                                    {(user.name || user.email?.split("@")[0] || "Unknown").slice(0, 20)}
                                    {user.is_super_admin && <Shield className="w-3 h-3 text-purple-500 shrink-0" />}
                                    {user.tags?.includes("demo") && <span className="px-1 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-700 rounded uppercase">demo</span>}
                                  </div>
                                  {!isColumnVisible("company") && user.company && (
                                    <p className="text-[10px] text-gray-500 truncate">{user.company}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                          )}
                          {/* Email */}
                          {isColumnVisible("email") && (
                            <td className="p-2 text-xs truncate">
                              <span className={`truncate block ${!showSensitiveData ? "blur-sm" : ""}`}>
                                {maskEmail(user.email) || "-"}
                              </span>
                            </td>
                          )}
                          {/* Phone */}
                          {isColumnVisible("phone") && (
                            <td className="p-2 text-xs">
                              <span className={!showSensitiveData ? "blur-sm" : ""}>
                                {user.phone || "-"}
                              </span>
                            </td>
                          )}
                          {/* Company */}
                          {isColumnVisible("company") && (
                            <td className="p-2 text-xs truncate">
                              <span className="text-gray-700 truncate block">{user.company || "-"}</span>
                            </td>
                          )}
                          {/* Location */}
                          {isColumnVisible("location") && (
                            <td className="p-2">
                              <span className="text-gray-500 text-[10px]">{user.location || user.city || "-"}</span>
                            </td>
                          )}
                          {/* Tags */}
                          {isColumnVisible("tags") && (
                            <td className="p-2">
                              <div className="flex flex-wrap gap-0.5">
                                {user.tags?.slice(0, 3).map((tag, idx) => (
                                  <Badge key={idx} variant="outline" className="text-[10px]">
                                    {tag.replace("pilot_", "").replace("role_", "")}
                                  </Badge>
                                ))}
                                {user.tags?.length > 3 && (
                                  <Badge variant="secondary" className="text-[10px]">+{user.tags.length - 3}</Badge>
                                )}
                              </div>
                            </td>
                          )}
                          {/* Preferences */}
                          {isColumnVisible("preferences") && (
                            <td className="p-2">
                              <div className="flex items-center gap-1.5">
                                {user.has_defaults ? (
                                  <div className="flex gap-0.5">
                                    {user.filled_categories?.map((cat) => {
                                      const config = CATEGORY_ICONS[cat];
                                      if (!config) return null;
                                      const Icon = config.icon;
                                      return (
                                        <div
                                          key={cat}
                                          className={`w-6 h-6 rounded-full ${config.bg} flex items-center justify-center`}
                                          title={cat}
                                        >
                                          <Icon className={`w-3 h-3 ${config.color}`} />
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-xs">None</span>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setPrefCardUser(user); }}
                                  className="p-1 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                                  title="Open preference card"
                                  data-testid={`pref-card-eye-${user.user_id || user.id}`}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          )}
                          {/* Beehiiv */}
                          {isColumnVisible("beehiiv") && (
                            <td className="p-2">
                              {user.beehiiv_subscriber_id ? (
                                <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5">
                                  Synced
                                </Badge>
                              ) : (
                                <span className="text-[10px] text-gray-400">No</span>
                              )}
                            </td>
                          )}
                          {/* Instagram */}
                          {isColumnVisible("instagram") && (
                            <td className="p-2 text-xs truncate">
                              {user.instagram ? (
                                <a 
                                  href={`https://instagram.com/${user.instagram.replace("@", "")}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-pink-600 hover:underline text-[10px] truncate block"
                                >
                                  @{user.instagram.replace("@", "")}
                                </a>
                              ) : (
                                <span className="text-gray-400 text-[10px]">-</span>
                              )}
                            </td>
                          )}
                          {/* LinkedIn */}
                          {isColumnVisible("linkedin") && (
                            <td className="p-2">
                              {user.linkedin ? (
                                <a 
                                  href={user.linkedin} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-blue-600 hover:underline text-[10px]"
                                >
                                  Link
                                </a>
                              ) : (
                                <span className="text-gray-400 text-[10px]">-</span>
                              )}
                            </td>
                          )}
                          {/* Events */}
                          {isColumnVisible("events") && (
                            <td className="p-2">
                              <div className="flex items-center gap-1 text-[10px]">
                                {user.events_hosted > 0 && (
                                  <Badge variant="secondary" className="text-[10px]">
                                    {user.events_hosted} hosted
                                  </Badge>
                                )}
                                {user.events_attended > 0 && (
                                  <span className="text-gray-500">{user.events_attended} attended</span>
                                )}
                                {!user.events_hosted && !user.events_attended && "-"}
                              </div>
                            </td>
                          )}
                          {/* Joined */}
                          {isColumnVisible("created_at") && (
                            <td className="p-2 text-gray-500 text-[10px]">
                              {user.created_at 
                                ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : "-"
                              }
                            </td>
                          )}
                          {/* Row actions (Iter 9.40): Edit + Copy magic link (persistent) */}
                          <td className="p-2 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="inline-flex items-center gap-1">
                              <button
                                onClick={() => setEditingUser(user)}
                                className="px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-[10px] font-medium text-gray-700 inline-flex items-center gap-1"
                                data-testid={`edit-user-${user.user_id}`}
                                title="Edit user"
                              >
                                <Edit2 className="w-3 h-3" />Edit
                              </button>
                              <button
                                onClick={() => handleCopyMagicLink(user.user_id, user.name)}
                                className="px-2 py-1 rounded-md bg-purple-100 hover:bg-purple-200 text-[10px] font-medium text-purple-700 inline-flex items-center gap-1"
                                data-testid={`copy-magic-link-${user.user_id}`}
                                title="Copy a never-expiring magic link to log in as this user (paste into incognito)"
                              >
                                <Link2 className="w-3 h-3" />Copy link
                              </button>
                            </div>
                          </td>
                        </tr>
                        {/* Inline expansion */}
                        {selectedUserId === user.user_id && (
                          <InlineUserExpansion 
                            userId={user.user_id} 
                            onClose={() => setSelectedUserId(null)} 
                          />
                        )}
                      </React.Fragment>
                    ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* CARD VIEW */
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="users-card-grid">
                  {filteredUsers.map(user => (
                    <div
                      key={user.user_id}
                      onClick={() => handleRowClick(user.user_id)}
                      className={`
                        p-4 border rounded-xl cursor-pointer transition-all hover:shadow-md hover:border-purple-200
                        ${selectedUserId === user.user_id ? "ring-2 ring-purple-500 border-purple-300" : ""}
                        ${selectedUserIds.includes(user.user_id) ? "bg-purple-50/50" : "bg-white"}
                      `}
                      data-testid={`user-card-${user.user_id}`}
                    >
                      {/* Card Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12 ring-2 ring-white shadow">
                            <AvatarImage src={user.picture || user.profile_image} />
                            <AvatarFallback className="bg-black text-white font-bold">
                              {getInitials(user.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold text-gray-900 flex items-center gap-1">
                              {user.name || user.email?.split("@")[0] || "Unknown"}
                              {user.is_super_admin && <Shield className="w-3.5 h-3.5 text-purple-500" />}
                            </h3>
                            {user.company && (
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {user.company}
                              </p>
                            )}
                          </div>
                        </div>
                        <Checkbox
                          checked={selectedUserIds.includes(user.user_id)}
                          onCheckedChange={(e) => handleToggleUser(user.user_id, { stopPropagation: () => {} })}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      
                      {/* Card Body */}
                      <div className="space-y-2 text-sm">
                        {/* Email */}
                        {isColumnVisible("email") && user.email && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                            <span className={`truncate ${!showSensitiveData ? "blur-sm" : ""}`}>
                              {maskEmail(user.email)}
                            </span>
                          </div>
                        )}
                        
                        {/* Phone */}
                        {isColumnVisible("phone") && user.phone && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                            <span className={!showSensitiveData ? "blur-sm" : ""}>{user.phone}</span>
                          </div>
                        )}
                        
                        {/* Location */}
                        {isColumnVisible("location") && (user.location || user.city) && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                            <span className="truncate">{user.location || user.city}</span>
                          </div>
                        )}
                        
                        {/* Social Links */}
                        {(isColumnVisible("instagram") || isColumnVisible("linkedin")) && (user.instagram || user.linkedin) && (
                          <div className="flex items-center gap-3">
                            {isColumnVisible("instagram") && user.instagram && (
                              <a 
                                href={`https://instagram.com/${user.instagram.replace("@", "")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-pink-600 hover:text-pink-700"
                              >
                                <Instagram className="w-4 h-4" />
                              </a>
                            )}
                            {isColumnVisible("linkedin") && user.linkedin && (
                              <a 
                                href={user.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Linkedin className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Card Footer - Tags & Status */}
                      <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2">
                        {/* Tags */}
                        {isColumnVisible("tags") && user.tags?.slice(0, 2).map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-[10px]">
                            {tag.replace("pilot_", "").replace("role_", "")}
                          </Badge>
                        ))}
                        {user.tags?.length > 2 && (
                          <Badge variant="secondary" className="text-[10px]">+{user.tags.length - 2}</Badge>
                        )}
                        
                        {/* Preferences indicator */}
                        {isColumnVisible("preferences") && user.has_defaults && (
                          <div className="flex gap-0.5 ml-auto">
                            {user.filled_categories?.slice(0, 3).map((cat) => {
                              const config = CATEGORY_ICONS[cat];
                              if (!config) return null;
                              const Icon = config.icon;
                              return (
                                <div
                                  key={cat}
                                  className={`w-5 h-5 rounded-full ${config.bg} flex items-center justify-center`}
                                  title={cat}
                                >
                                  <Icon className={`w-2.5 h-2.5 ${config.color}`} />
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Beehiiv status */}
                        {isColumnVisible("beehiiv") && user.beehiiv_subscriber_id && (
                          <Globe className="w-4 h-4 text-orange-500 ml-auto" title="Beehiiv Synced" />
                        )}
                      </div>
                      
                      {/* Joined date */}
                      {isColumnVisible("created_at") && user.created_at && (
                        <div className="mt-2 flex items-center gap-1 text-[10px] text-gray-400">
                          <Calendar className="w-3 h-3" />
                          Joined {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
            </>
          )}
          
          {/* Waitlist View */}
          {mainView === "waitlist" && (
            <WaitlistManager />
          )}

          {/* Host Pipeline (Kanban) View */}
          {mainView === "pipeline" && (
            <>
              <div className="flex items-center justify-end mb-3 gap-2">
                <Button onClick={() => setShowCreateDashboard(true)} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="create-dashboard-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Create Dashboard (any category)
                </Button>
              </div>
              <HostPipelineKanban
                users={users}
                onUserUpdated={() => fetchUsers()}
                onAddHost={() => setShowAddHost(true)}
                onEditUser={(u) => setEditingUser(u)}
              />
            </>
          )}

          {/* Organizations View */}
          {mainView === "organizations" && (
            <Card className="shadow-lg border-gray-200">
              <CardContent className="p-4 md:p-6">
                {/* Organizations Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Input
                      placeholder="Search organizations..."
                      value={orgSearch}
                      onChange={(e) => setOrgSearch(e.target.value)}
                      className="w-64"
                      data-testid="org-search"
                    />
                    <Button variant="outline" size="sm" onClick={fetchOrganizations}>
                      <RefreshCw className={`w-4 h-4 ${orgsLoading ? "animate-spin" : ""}`} />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{allOrganizations.length} organizations</span>
                    <Button size="sm" onClick={() => setShowCreateOrgDialog(true)} data-testid="new-org-btn">
                      <Plus className="w-4 h-4 mr-1" /> New Org
                    </Button>
                  </div>
                </div>
                
                {orgsLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Organizations List */}
                    <div className="space-y-2 border-r pr-4">
                      <h3 className="font-semibold text-gray-700 mb-3">All Organizations</h3>
                      {allOrganizations
                        .filter(org => !orgSearch || org.name?.toLowerCase().includes(orgSearch.toLowerCase()))
                        .map((org) => (
                          <div
                            key={org.org_id}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                              selectedOrg?.org_id === org.org_id 
                                ? "bg-blue-50 border-blue-300" 
                                : "hover:bg-gray-50 border-gray-200"
                            }`}
                            onClick={() => {
                              setSelectedOrg(org);
                              setEditingOrg(null);
                            }}
                            data-testid={`org-row-${org.org_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-gray-400" />
                                <div>
                                  <span className="font-medium">{org.name}</span>
                                  <span className="text-xs text-gray-400 ml-2">({org.type || "company"})</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="text-xs">
                                  {org.member_count || 0} members
                                </Badge>
                                {(org.linked_dashboards || 0) > 0 && (
                                  <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                                    {org.linked_dashboards} dashboard{org.linked_dashboards > 1 ? 's' : ''}
                                  </Badge>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedOrg(org); // Also select the org
                                    setEditingOrg(org);
                                    setEditOrgData({ 
                                      name: org.name, 
                                      description: org.description || "",
                                      website: org.website || "",
                                      logo_url: org.logo_url || "",
                                      linked_categories: org.linked_categories || []
                                    });
                                  }}
                                  data-testid={`edit-org-${org.org_id}`}
                                >
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            {org.description && (
                              <p className="text-xs text-gray-500 mt-1 truncate">{org.description}</p>
                            )}
                            {/* Host Dashboard Categories */}
                            {(org.linked_categories || []).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {org.linked_categories.map(catKey => {
                                  const preset = getCategoryPreset(catKey);return (
                                    <span key={catKey} className="inline-block px-1.5 py-0.5 rounded-full text-[9px] text-white" style={{ backgroundColor: preset?.iconColor || "#9ca3af" }}>
                                      {preset?.name || catKey}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      {allOrganizations.length === 0 && (
                        <p className="text-center text-gray-400 py-8">No organizations yet</p>
                      )}
                    </div>
                    
                    {/* Selected Org Members */}
                    <div className="pl-4">
                      {selectedOrg ? (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-gray-700">
                              Members of {selectedOrg.name}
                            </h3>
                            <div className="flex items-center gap-2">
                              <Badge>{orgMembers.length} people</Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => setShowAddMemberSearch(!showAddMemberSearch)}
                                data-testid="add-member-btn"
                              >
                                <Plus className="w-3 h-3 mr-1" /> Add Member
                              </Button>
                            </div>
                          </div>
                          
                          {/* In-row Linked Dashboards CRUD */}
                          <OrgLinkedDashboardsPanel orgId={selectedOrg.org_id} orgName={selectedOrg.name} />

                          {/* Edit Org Form - Always show when editingOrg matches */}
                          {editingOrg?.org_id === selectedOrg.org_id && (<div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <h4 className="text-xs font-semibold text-yellow-700 mb-2">Edit Organization</h4>
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <Label className="text-xs">Host Name *</Label>
                                    <Input
                                      value={editOrgData.name}
                                      onChange={(e) => setEditOrgData(prev => ({ ...prev, name: e.target.value }))}
                                      className="text-sm"
                                      data-testid="edit-org-name"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Website URL</Label>
                                    <Input
                                      value={editOrgData.website}
                                      onChange={(e) => setEditOrgData(prev => ({ ...prev, website: e.target.value }))}
                                      className="text-sm"
                                      placeholder="https://example.com"
                                      data-testid="edit-org-website"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-xs">Logo</Label>
                                  <ImageUpload
                                    value={editOrgData.logo_url}
                                    onChange={(url) => setEditOrgData(prev => ({ ...prev, logo_url: url }))}
                                    previewSize="small"
                                    data-testid="edit-org-logo"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Description</Label>
                                  <Textarea
                                    value={editOrgData.description}
                                    onChange={(e) => setEditOrgData(prev => ({ ...prev, description: e.target.value }))}
                                    rows={2}
                                    className="text-sm"
                                  />
                                </div>
                                {/* Host Dashboard Categories */}
                                <div>
                                  <Label className="text-xs">Linked Host Dashboards</Label>
                                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                                    {getAllCategories().map(cat => {
                                      const isLinked = (editOrgData.linked_categories || []).includes(cat.key);
                                      return (
                                        <button
                                          key={cat.key}
                                          type="button"
                                          onClick={() => {
                                            setEditOrgData(prev => ({
                                              ...prev,
                                              linked_categories: isLinked
                                                ? (prev.linked_categories || []).filter(c => c !== cat.key)
                                                : [...(prev.linked_categories || []), cat.key]
                                            }));
                                          }}
                                          className="px-2 py-0.5 rounded-full text-[10px] border transition-colors"
                                          style={isLinked ? { backgroundColor: cat.iconColor, color: "white", borderColor: "transparent" } : {}}
                                          data-testid={`edit-org-cat-${cat.key}`}
                                        >
                                          {cat.name}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="flex gap-2 justify-between">
                                  <Button 
                                    variant="destructive" 
                                    size="sm" 
                                    onClick={async () => {
                                      if (!window.confirm(`Are you sure you want to delete "${editingOrg.name}"? This will remove the organization and all member associations.`)) {
                                        return;
                                      }
                                      try {
                                        const res = await fetch(`${API}/admin/organizations/${editingOrg.org_id}`, {
                                          method: "DELETE",
                                          headers: getHeaders()
                                        });
                                        if (res.ok) {
                                          toast.success("Organization deleted");
                                          setEditingOrg(null);
                                          setSelectedOrg(null);
                                          fetchOrganizations();
                                        } else {
                                          toast.error("Failed to delete organization");
                                        }
                                      } catch (e) {
                                        toast.error("Failed to delete organization");
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3 mr-1" />
                                    Delete Org
                                  </Button>
                                  <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setEditingOrg(null)}>
                                      Cancel
                                    </Button>
                                    <Button size="sm" onClick={handleUpdateOrg} disabled={savingOrg}>
                                      <Save className="w-3 h-3 mr-1" />
                                      {savingOrg ? "Saving..." : "Save Changes"}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Add Member Search */}
                          {showAddMemberSearch && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <Label className="text-xs font-medium">Search People to Add</Label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowAddMemberSearch(false);
                                    setMemberSearchQuery("");
                                    setMemberSearchResults([]);
                                  }}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <Input
                                value={memberSearchQuery}
                                onChange={(e) => {
                                  setMemberSearchQuery(e.target.value);
                                  searchUsersForOrg(e.target.value);
                                }}
                                placeholder="Search by name or email..."
                                className="text-sm mb-2"
                                data-testid="member-search-input"
                              />
                              {searchingMembers && <p className="text-xs text-gray-400">Searching...</p>}
                              {memberSearchResults.length > 0 && (
                                <div className="max-h-40 overflow-y-auto space-y-1">
                                  {memberSearchResults.map((user) => (
                                    <button
                                      key={user.user_id}
                                      type="button"
                                      className="w-full px-2 py-1.5 text-left text-sm hover:bg-blue-100 rounded flex items-center justify-between"
                                      onClick={() => handleAddMemberToOrg(user)}
                                      disabled={addingMember}
                                    >
                                      <div>
                                        <span className="font-medium">{user.name || user.email}</span>
                                        {user.company && <span className="text-xs text-gray-400 ml-2">{user.company}</span>}
                                      </div>
                                      <Plus className="w-3 h-3 text-blue-500" />
                                    </button>
                                  ))}
                                </div>
                              )}
                              {memberSearchQuery.length >= 2 && memberSearchResults.length === 0 && !searchingMembers && (
                                <p className="text-xs text-gray-400">No users found</p>
                              )}
                            </div>
                          )}
                          
                          {/* Members List */}
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {orgMembers.map((member) => (
                              <div
                                key={member.user_id}
                                className="p-2 bg-gray-50 rounded flex items-center justify-between hover:bg-gray-100 cursor-pointer"
                                onClick={() => {
                                  setSelectedUserId(member.user_id);
                                  setMainView("users");
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                                    {member.name?.slice(0, 2).toUpperCase() || "?"}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium">{member.name || member.email}</p>
                                    <p className="text-xs text-gray-400">{member.role || "member"}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {/* Inspo Preview */}
                                  {member.inspo_count > 0 && (
                                    <div className="flex items-center gap-1">
                                      <Image className="w-3 h-3 text-purple-500" />
                                      <span className="text-xs text-purple-500">{member.inspo_count}</span>
                                    </div>
                                  )}
                                  {/* Remove Button */}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveMemberFromOrg(member.user_id, member.name || member.email);
                                    }}
                                    data-testid={`remove-member-${member.user_id}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                            {orgMembers.length === 0 && (
                              <p className="text-center text-gray-400 py-4">No members in this organization</p>
                            )}
                          </div>
                          
                          {/* Org Events Section */}
                          <OrgEventsSection orgId={selectedOrg.org_id} />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-48 text-gray-400">
                          <div className="text-center">
                            <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>Select an organization to view members</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Host Dashboards View */}
          {mainView === "dashboards" && (
            <HostDashboardsTab />
          )}
          
          {/* /me Pilots View */}
          {mainView === "me-pilots" && (
            <MePilotsTab />
          )}
          
          {/* Portal Access View */}
          {mainView === "portal-access" && (
            <PortalAccessTab />
          )}
          
          {/* Inner Circle View */}
          {mainView === "inner-circle" && (
            <InnerCircleSection />
          )}
          
          {/* Fields & Tags View */}
          {mainView === "fields" && (
            <Card className="shadow-lg border-gray-200">
              <CardContent className="p-4 md:p-6">
                {/* Live Stats Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                    <p className="text-2xl font-bold text-purple-700">{users.length}</p>
                    <p className="text-xs text-purple-600">Total Users</p>
                  </div>
                  <div className="p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                    <p className="text-2xl font-bold text-green-700">{dynamicTags.tags?.length || 0}</p>
                    <p className="text-xs text-green-600">Total Tags</p>
                  </div>
                  <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                    <p className="text-2xl font-bold text-blue-700">{dynamicTags.categories?.pilots?.length || 8}</p>
                    <p className="text-xs text-blue-600">Pilot Categories</p>
                  </div>
                  <div className="p-3 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
                    <p className="text-2xl font-bold text-orange-700">{beehiivFields.length}</p>
                    <p className="text-xs text-orange-600">Beehiiv Fields</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Platform Fields & Tags */}
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Phygital Platform Fields
                      <Badge variant="outline" className="text-[10px]">Live Data</Badge>
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="p-2 bg-gray-50 rounded">
                        <p className="font-medium text-gray-700">User Profile Fields</p>
                        <ul className="mt-1 space-y-1 text-gray-500 text-xs">
                          <li>• name (canonical full name), email, phone</li>
                          <li>• company, title, bio, location, city</li>
                          <li>• instagram, linkedin, tiktok, website</li>
                          <li>• <span className="text-purple-600 font-medium">personal_socials</span>, <span className="text-purple-600 font-medium">business_socials</span> (NEW)</li>
                          <li>• role, role_title, tags[], organizations[]</li>
                          <li>• <span className="text-blue-600 font-medium">invited_by</span>, <span className="text-blue-600 font-medium">referral_source</span>, <span className="text-blue-600 font-medium">referral_count</span> (NEW)</li>
                          <li>• beehiiv_subscriber_id, beehiiv_status, beehiiv_tags[]</li>
                        </ul>
                      </div>
                      <div className="p-2 bg-blue-50 rounded">
                        <p className="font-medium text-blue-700">Preference Categories (user_defaults)</p>
                        <ul className="mt-1 space-y-1 text-blue-600 text-xs">
                          <li>• <Utensils className="w-3 h-3 inline" /> dietary: dietary_restrictions[], allergies[], meat_preferences[]</li>
                          <li>• <Accessibility className="w-3 h-3 inline" /> accessibility: physical_needs[], invisible_disabilities[]</li>
                          <li>• <Heart className="w-3 h-3 inline" /> interests: interests[], vibe</li>
                          <li>• <Scissors className="w-3 h-3 inline" /> hair: hair_type, hair_texture, hair_length, hair_color</li>
                          <li>• <Sparkles className="w-3 h-3 inline" /> skin: skin_type, skin_tone, skin_concerns[]</li>
                          <li>• <Shirt className="w-3 h-3 inline text-violet-500" /> <span className="text-violet-600 font-medium">style: style_options[], shoe_options[], jewelry_options[], metal_options[] (NEW)</span></li>
                        </ul>
                      </div>
                      <div className="p-2 bg-green-50 rounded">
                        <p className="font-medium text-green-700">Event/RSVP/Pilot Fields</p>
                        <ul className="mt-1 space-y-1 text-green-600 text-xs">
                          <li>• rsvps: event_id, event_name, status, checked_in_at</li>
                          <li>• pilot_intakes: pilot_type, status, intake_completed, nda_signed</li>
                          <li>• inner_circle: list_name, source, added_at</li>
                          <li>• experiences: inspo images, categories, notes</li>
                        </ul>
                      </div>
                    </div>
                    
                    {/* Platform Tags */}
                    <h4 className="font-semibold text-gray-700 mt-4 mb-2 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Platform Tags {tagsLoading && <RefreshCw className="w-3 h-3 animate-spin" />}
                      <span className="text-xs text-gray-400 font-normal">({dynamicTags.tags?.length || 0} total)</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-auto text-xs h-6"
                        onClick={() => {
                          const newTag = prompt("Enter new tag name (use snake_case):");
                          if (newTag && newTag.trim()) {
                            const tagName = newTag.trim().toLowerCase().replace(/\s+/g, '_');
                            // Add tag via API
                            fetch(`${API}/admin/tags`, {
                              method: "POST",
                              headers: { ...getHeaders(), "Content-Type": "application/json" },
                              body: JSON.stringify({ tag: tagName, sync_to_beehiiv: true })
                            }).then(res => {
                              if (res.ok) {
                                toast.success(`Tag "${tagName}" added and synced to Beehiiv`);
                                fetchDynamicTags();
                              } else {
                                toast.error("Failed to add tag");
                              }
                            }).catch(() => toast.error("Error adding tag"));
                          }
                        }}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Tag
                      </Button>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="p-2 bg-purple-50 rounded">
                        <p className="font-medium text-purple-700 text-xs">Role Tags (Multi-select)</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(dynamicTags.categories?.roles || ["host", "instructor", "speaker", "vip", "cohost", "provider", "studio_owner", "admin", "super_admin", "customer"]).map(tag => (
                            <Badge 
                              key={tag} 
                              variant="outline" 
                              className="text-[10px] bg-purple-100 cursor-pointer hover:bg-purple-200 group"
                              onClick={() => {
                                if (confirm(`Edit or Delete tag "${tag}"?\n\nClick OK to delete, Cancel to keep.`)) {
                                  fetch(`${API}/admin/tags/${tag}`, {
                                    method: "DELETE",
                                    headers: getHeaders()
                                  }).then(res => {
                                    if (res.ok) {
                                      toast.success(`Tag "${tag}" removed`);
                                      fetchDynamicTags();
                                    } else {
                                      toast.error("Cannot delete system tag");
                                    }
                                  });
                                }
                              }}
                            >
                              {tag}
                              <X className="w-2 h-2 ml-1 opacity-0 group-hover:opacity-100" />
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="p-2 bg-green-50 rounded">
                        <p className="font-medium text-green-700 text-xs">Pilot Tags (with Retail NEW)</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(dynamicTags.categories?.pilots || ["pilot_hair", "pilot_nails", "pilot_makeup", "pilot_lashes", "pilot_wellness", "pilot_events", "pilot_instructor", "pilot_culinary", "pilot_retail"]).map(tag => (
                            <Badge 
                              key={tag} 
                              variant="outline" 
                              className={`text-[10px] cursor-pointer hover:opacity-80 group ${tag === 'pilot_retail' ? 'bg-rose-100 text-rose-700' : 'bg-green-100'}`}
                              onClick={() => {
                                if (confirm(`Edit or Delete tag "${tag}"?\n\nClick OK to delete, Cancel to keep.`)) {
                                  fetch(`${API}/admin/tags/${tag}`, {
                                    method: "DELETE",
                                    headers: getHeaders()
                                  }).then(res => {
                                    if (res.ok) {
                                      toast.success(`Tag "${tag}" removed`);
                                      fetchDynamicTags();
                                    } else {
                                      toast.error("Cannot delete system tag");
                                    }
                                  });
                                }
                              }}
                            >
                              {tag}
                              <X className="w-2 h-2 ml-1 opacity-0 group-hover:opacity-100" />
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="p-2 bg-yellow-50 rounded">
                        <p className="font-medium text-yellow-700 text-xs">Status Tags</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(dynamicTags.categories?.status || ["verified", "active", "inactive", "test_account"]).map(tag => (
                            <Badge 
                              key={tag} 
                              variant="outline" 
                              className="text-[10px] bg-yellow-100 cursor-pointer hover:bg-yellow-200 group"
                              onClick={() => {
                                if (confirm(`Delete tag "${tag}"?`)) {
                                  fetch(`${API}/admin/tags/${tag}`, {
                                    method: "DELETE",
                                    headers: getHeaders()
                                  }).then(res => {
                                    if (res.ok) {
                                      toast.success(`Tag "${tag}" removed`);
                                      fetchDynamicTags();
                                    } else {
                                      toast.error("Cannot delete system tag");
                                    }
                                  });
                                }
                              }}
                            >
                              {tag}
                              <X className="w-2 h-2 ml-1 opacity-0 group-hover:opacity-100" />
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="p-2 bg-blue-50 rounded">
                        <p className="font-medium text-blue-700 text-xs">Custom Tags (click to delete)</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(dynamicTags.categories?.custom || []).slice(0, 15).map(tag => (
                            <Badge 
                              key={tag} 
                              variant="outline" 
                              className="text-[10px] bg-blue-100 cursor-pointer hover:bg-red-100 hover:text-red-700 group"
                              onClick={() => {
                                if (confirm(`Delete custom tag "${tag}"?\n\nThis will also remove it from Beehiiv.`)) {
                                  fetch(`${API}/admin/tags/${tag}`, {
                                    method: "DELETE",
                                    headers: getHeaders()
                                  }).then(res => {
                                    if (res.ok) {
                                      toast.success(`Tag "${tag}" removed from Phygital and Beehiiv`);
                                      fetchDynamicTags();
                                    } else {
                                      toast.error("Failed to delete tag");
                                    }
                                  });
                                }
                              }}
                            >
                              {tag}
                              <X className="w-2 h-2 ml-1 opacity-0 group-hover:opacity-100" />
                            </Badge>
                          ))}
                          {(dynamicTags.categories?.custom?.length || 0) > 15 && (
                            <Badge variant="outline" className="text-[10px]">+{dynamicTags.categories.custom.length - 15} more</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Beehiiv Custom Fields & Sync */}
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-orange-500" />
                      Beehiiv Newsletter Fields {beehiivFieldsLoading && <RefreshCw className="w-3 h-3 animate-spin" />}
                      <span className="text-xs text-gray-400 font-normal">({beehiivFields.length} custom fields)</span>
                    </h3>
                    
                    {/* Beehiiv Custom Fields List */}
                    <div className="p-3 bg-orange-50 rounded border border-orange-200 mb-3 max-h-64 overflow-y-auto">
                      <p className="font-medium text-orange-700 mb-2 text-sm">Custom Fields in Beehiiv</p>
                      {beehiivFields.length > 0 ? (
                        <div className="grid grid-cols-2 gap-1">
                          {beehiivFields.map((field, idx) => (
                            <div key={field.id || idx} className="text-xs p-1.5 bg-white rounded border border-orange-100 flex items-center justify-between">
                              <span className="font-medium text-gray-700 truncate">{field.name}</span>
                              <span className="text-[9px] text-orange-500 ml-1">{field.kind}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-orange-600">Loading Beehiiv fields...</p>
                      )}
                    </div>
                    
                    {/* Field Mapping Reference */}
                    <div className="p-3 bg-gray-50 rounded border mb-3">
                      <p className="font-medium text-gray-700 mb-2 text-sm">Field Mappings (Phygital → Beehiiv)</p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-gray-500">name (split on first space)</span><span className="font-mono text-orange-600">First Name / Last Name</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">company</span><span className="font-mono text-orange-600">company</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">title</span><span className="font-mono text-orange-600">title</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">city</span><span className="font-mono text-orange-600">user_city</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">org_name</span><span className="font-mono text-orange-600">business_name</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">phone</span><span className="font-mono text-orange-600">phone</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">instagram</span><span className="font-mono text-orange-600">instagram_handle</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">dietary_restrictions</span><span className="font-mono text-orange-600">dietary_restrictions</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">accessibility_needs</span><span className="font-mono text-orange-600">accessibility_needs</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">interests</span><span className="font-mono text-orange-600">interests</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">last_event_name</span><span className="font-mono text-orange-600">last_event</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">event_date</span><span className="font-mono text-orange-600">event_date</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">user_id</span><span className="font-mono text-orange-600">phygital_user_id</span></div>
                      </div>
                    </div>
                    
                    {/* Tag Prefixes for Beehiiv */}
                    <div className="p-3 bg-purple-50 rounded border border-purple-200 mb-3">
                      <p className="font-medium text-purple-700 mb-2 text-sm">Tag Prefixes (auto-generated)</p>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <div className="p-1 bg-white rounded">interest_*</div>
                        <div className="p-1 bg-white rounded">dietary_*</div>
                        <div className="p-1 bg-white rounded">access_*</div>
                        <div className="p-1 bg-white rounded">event_*</div>
                        <div className="p-1 bg-white rounded">source_*</div>
                        <div className="p-1 bg-white rounded">role_*</div>
                        <div className="p-1 bg-white rounded">pilot_*</div>
                        <div className="p-1 bg-white rounded">stage_*</div>
                      </div>
                    </div>
                    
                    {/* Sync Status */}
                    <div className="p-3 bg-green-50 rounded border border-green-200">
                      <p className="font-medium text-green-700 mb-2 text-sm">Sync Status</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Total Users</span>
                          <span className="font-medium">{users.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Synced to Beehiiv</span>
                          <span className="font-medium text-green-600">{users.filter(u => u.beehiiv_subscriber_id).length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Not Synced</span>
                          <span className="font-medium text-red-600">{users.filter(u => !u.beehiiv_subscriber_id).length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Bottom action buttons */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setMainView("people")}
                  >
                    Go to People Tab
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      fetchDynamicTags();
                      fetchBeehiivFields();
                    }}
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Refresh Fields
                  </Button>
                  <Button 
                    variant="default"
                    size="sm"
                    className="bg-orange-500 hover:bg-orange-600"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/beehiiv/pull-subscribers`, {
                          method: "POST",
                          headers: getHeaders()
                        });
                        if (res.ok) {
                          toast.success("Pull from Beehiiv started! Check sync log for progress.");
                        } else {
                          toast.error("Failed to start Beehiiv pull");
                        }
                      } catch (e) {
                        toast.error("Error starting Beehiiv pull");
                      }
                    }}
                  >
                    <Download className="w-3 h-3 mr-1" /> Pull from Beehiiv
                  </Button>
                  <Button 
                    variant="default"
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/beehiiv/sync-all`, {
                          method: "POST",
                          headers: getHeaders()
                        });
                        if (res.ok) {
                          toast.success("Sync to Beehiiv started! Check sync log for progress.");
                        } else {
                          toast.error("Failed to start Beehiiv sync");
                        }
                      } catch (e) {
                        toast.error("Error starting Beehiiv sync");
                      }
                    }}
                  >
                    <Upload className="w-3 h-3 mr-1" /> Push to Beehiiv
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Universal Preference Card popup - Iter 9.40 Slice C⁶. Same component the
          Kanban template uses, so admin sees exactly what the host sees. */}
      <UniversalPreferenceCardPopup
        open={!!prefCardUser}
        onOpenChange={(o) => { if (!o) setPrefCardUser(null); }}
        client={prefCardUser ? {
          name: prefCardUser.name || `${prefCardUser.first_name || ""} ${prefCardUser.last_name || ""}`.trim() || prefCardUser.email,
          avatar_url: prefCardUser.profile_image_url || prefCardUser.avatar_url,
          service: prefCardUser.primary_category ? `${prefCardUser.primary_category} preferences` : null,
          date: prefCardUser.last_active_at || prefCardUser.created_at,
        } : null}
        inspo={prefCardUser?.preferences?.inspo || []}
        tagLabels={(prefCardUser?.filled_categories || []).slice(0, 6)}
        linkedProducts={prefCardUser?.preferences?.product_links || []}
        finalLooks={prefCardUser?.preferences?.final_looks || []}
      />

      {/* Detail Panel - Full overlay, does not push main content */}
      {panelOpen && (
        <>
          {/* Backdrop overlay */}
          <div 
            className="fixed inset-0 bg-black/20 z-30" 
            onClick={() => { setPanelOpen(false); setSelectedUserId(null); }}
            data-testid="panel-backdrop"
          />
          <div className="fixed right-0 top-0 bottom-0 w-full max-w-[540px] z-40 shadow-2xl overflow-hidden">
            <UserDetailPanel
              userId={selectedUserId}
              onClose={() => {
                setPanelOpen(false);
                setSelectedUserId(null);
              }}
              onUpdate={fetchUsers}
              showSensitiveData={showSensitiveData}
            />
          </div>
        </>
      )}
      
      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrgDialog} onOpenChange={setShowCreateOrgDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Create New Organization
            </DialogTitle>
            <DialogDescription>
              Add a new organization that can have pilots, events, and team members
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Host Name *</Label>
              <Input
                value={newOrg.name}
                onChange={(e) => setNewOrg(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Your Company, Partner Org"
                data-testid="new-org-name"
              />
            </div>
            
            <div>
              <Label>Type</Label>
              <Select 
                value={newOrg.org_type} 
                onValueChange={(v) => setNewOrg(prev => ({ ...prev, org_type: v }))}
              >
                <SelectTrigger data-testid="new-org-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                  <SelectItem value="brand">Brand</SelectItem>
                  <SelectItem value="studio">Studio</SelectItem>
                  <SelectItem value="collective">Collective</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={newOrg.description}
                onChange={(e) => setNewOrg(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the organization..."
                rows={3}
                data-testid="new-org-description"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateOrgDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrg} disabled={creatingOrg || !newOrg.name.trim()} data-testid="submit-create-org">
              {creatingOrg ? "Creating..." : "Create Organization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Universal Edit User Modal */}
      <RecordEditModal
        open={!!editingUser}
        onClose={() => setEditingUser(null)}
        record={editingUser}
        title={editingUser ? `Edit ${editingUser.name || editingUser.email || editingUser.user_id}` : "Edit User"}
        endpoint={editingUser ? `/admin/users/${editingUser.user_id}` : ""}
        fields={[
          { name: "first_name", label: "First Name", type: "text" },
          { name: "last_name", label: "Last Name", type: "text" },
          { name: "display_name", label: "Display Name (auto from First + Last)", type: "text" },
          { name: "email", label: "Email", type: "email" },
          { name: "phone", label: "Phone", type: "text" },
          { name: "is_pilot", label: "Pilot tier", type: "boolean", help: "Marks user as legacy pilot (free for life, bypasses Stripe)" },
          { name: "is_host", label: "Host (auto-set when a dashboard is activated)", type: "boolean", help: "Read-only-ish flag. Becomes true automatically when admin clicks Activate Dashboard for this user. Tells the platform to treat them as an active host (counts in stats, shows host UI, etc.)." },
          { name: "is_admin", label: "Admin", type: "boolean" },
          { name: "plan", label: "Plan", type: "select", options: [
            { value: "", label: "- none -" },
            { value: "legacy_pilot_free", label: "Legacy Pilot (free)" },
            { value: "solo", label: "Solo" },
            { value: "pro", label: "Pro" },
            { value: "enterprise", label: "Enterprise" },
          ]},
          { name: "primary_category", label: "Primary Category", type: "select", options: [
            "makeup","hair","nails","lashes","culinary","wellness","events","travel","style","instructor","dispensary",
          ]},
          { name: "org_id", label: "Organization", type: "ref", refEndpoint: "/admin/organizations", refKey: "org_id", refLabel: "name" },
        ]}
        onSaved={() => fetchUsers()}
      />

      {/* Add Host Modal */}
      <AddHostModal
        open={showAddHost}
        onClose={() => setShowAddHost(false)}
        onCreated={() => fetchUsers()}
      />

      {/* Create Dashboard Wizard - quick plug-and-play for any category */}
      <CreateDashboardWizardModal
        open={showCreateDashboard}
        onOpenChange={setShowCreateDashboard}
        onCreated={() => fetchUsers()}
      />
    </div>
  );
}


function InstructorsSection() {
  const [instructors, setInstructors] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState("all");

  useEffect(() => {
    fetchInstructorData();
  }, []);

  const fetchInstructorData = async () => {
    setLoading(true);
    const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
    try {
      const [eventsRes, rsvpsRes] = await Promise.all([
        fetch(`${API}/events?category=instructor`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/instructor-rsvps`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (eventsRes.ok) setEvents(await eventsRes.json());
      if (rsvpsRes.ok) {
        const data = await rsvpsRes.json();
        setInstructors(Array.isArray(data) ? data : data.rsvps || []);
      }
    } catch (err) {
      console.error("Failed to fetch instructor data:", err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = selectedEvent === "all" ? instructors : instructors.filter((i) => i.event_id === selectedEvent);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <Card data-testid="instructors-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <HeartHandshake className="w-5 h-5 text-blue-600" />
            Instructor Intakes
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{filtered.length} instructors</Badge>
            <select
              className="text-sm border rounded-lg px-3 py-1.5"
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              data-testid="instructor-event-filter"
            >
              <option value="all">All Events</option>
              {(Array.isArray(events) ? events : []).map((e) => (
                <option key={e.event_id} value={e.event_id}>{e.title || e.event_id}</option>
              ))}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <HeartHandshake className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No instructor intakes yet</p>
            <p className="text-xs mt-1">Instructor RSVPs will appear here when participants complete the instructor pilot intake flow.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((inst, idx) => (
              <div key={idx} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50 transition-colors" data-testid={`instructor-row-${idx}`}>
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {inst.preferences?.profile_photo_url ? (
                    <img src={inst.preferences.profile_photo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm font-bold text-blue-600">
                      {(inst.first_name || inst.user_name || "?")[0].toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{inst.first_name || inst.user_name || "Unknown"} {inst.last_name || ""}</p>
                  <p className="text-xs text-gray-500">{inst.email || ""}</p>
                </div>
                <div className="text-xs text-gray-400">{inst.preferences?.company || ""}</div>
                <div className="text-xs text-gray-400">{inst.preferences?.city || ""}</div>
                <Badge variant="outline" className="text-xs">
                  {inst.preferences?.builder_type || "Builder"}
                </Badge>
                <Badge className={inst.checked_in ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}>
                  {inst.checked_in ? "Checked In" : "RSVP'd"}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
